import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CompanyEnums } from 'src/app/enum/company-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { FormatDetailCaptureTableModel } from 'src/app/models/tables/format/format-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-format-detail',
  templateUrl: './format-detail.component.html',
  styleUrls: ['./format-detail.component.scss'],
  standalone: false
})
export class FormatDetailComponent implements OnInit, OnChanges {

  @Input() companyId: number = 0;
  @Input() disabledDetail: boolean = false;
  @Input() formatModel: FormatModel = new FormatModel();
  @Input() updateListDetail: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<FormatDetailCaptureTableModel> = new PagedResponseModel<FormatDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  formatDetailModel: FormatDetailModel = new FormatDetailModel();
  filters: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  limit: number = 10;
  page: number = 1;
  showCheckbox: boolean = true;
  showNewDetail: boolean = false;
  tableActions: TableAction[] = [];

  constructor(private confirmDialogService: ConfirmDialogService, private formatDetailService: FormatDetailService, private toastHandlerService: ToastHandlerService) { }

  async ngOnInit(): Promise<void> {
    await this.getColumns();
    this.tableActions = this.getDefaultActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    const updateListDetail = changes["updateListDetail"];
    if (updateListDetail && !updateListDetail.firstChange) {
      this.getFormatDetailsByFormat();
    }
    const formatModel = changes["formatModel"];
    if (formatModel && !formatModel.firstChange) {
      this.getFormView(false);
      this.getFormatDetailsByFormat();
      this.formatDetailModel = new FormatDetailModel();
      this.tableActions = this.disabledDetail ? this.getDefaultActions() : this.getDefaultActions();
    }
    const companyId = changes["companyId"];
    if (companyId) {
      if (companyId.currentValue === CompanyEnums.altosSoftware) {
        this.showNewDetail = true;
        this.showCheckbox = true;
      } else {
        this.showNewDetail = false;
        this.showCheckbox = false;
      }
      this.isFormView = false;
      this.detailData = new PagedResponseModel<FormatDetailCaptureTableModel>();
    }
  }

  async createNewDetail() {
    this.isFormView = true;
    this.formatDetailModel = new FormatDetailModel();
  }

  async deleteFormatDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    if (this.companyId !== CompanyEnums.altosSoftware) {
      this.toastHandlerService.showToast(
        'error',
        'Solo está permitido crear formatos en la compañía principal.'
      );
      return;
    }

    if (await this.confirmDialogService.confirm(`Esta acción elimina el detalle de todos los formatos de ${this.formatModel.name} y no se puede deshacer. ¿Realmente desea eliminar el detalle del formato?`)) {
      try {
        await firstValueFrom(this.formatDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle fue eliminado exitosamente.',
          undefined,
          undefined,
          5000
        );
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        this.getUpdateListDetailByDelete();
        console.error('Error al eliminar los detalles del formato.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.formatDetailService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error al obtener las columnas para la tabla del detalle.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      },
    ];
  }

  getDefaultActionsDisabled(): TableAction[] {
    return [];
  }

  async getFormatDetailsByFormat() {
    try {
      this.detailData = await firstValueFrom(
        this.formatDetailService.getFormatDetailsForCaptureTable(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.formatModel.formatId
        )
      );
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al obtener los detalles del formato.', error);
    }
  }

  async getFormatDetailById(formatDetailId: number) {
    try {
      this.formatDetailModel = await firstValueFrom(this.formatDetailService.getFormatDetailById(formatDetailId));
    } catch (error) {
      console.error('Error al obtener el detalle del formato por ID.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getUpdateListDetailByDelete() {
    this.formatDetailModel = new FormatDetailModel();
    this.clearSelectionTrigger++;
    this.getFormatDetailsByFormat();
  }

  async onAction(event: { action: string, item?: FormatDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      this.getFormView(true);
      await this.getFormatDetailById(event.item!.id);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteFormatDetail(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getFormatDetailsByFormat();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
